import { useEffect, useState, useRef } from 'react';
import {
  ServerIcon,
  CpuChipIcon,
  CircleStackIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import { PageLoader, StatusBadge } from '@/components/common';
import { metricsApi } from '@/services/api';
import type { ServerMetrics, ServiceHealth } from '@/types';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ') || '< 1m';
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  percentage?: number;
}

function MetricCard({ title, value, subtitle, icon: Icon, color, percentage }: MetricCardProps) {
  return (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
        {percentage !== undefined && (
          <span
            className={`text-sm font-medium ${
              percentage > 80 ? 'text-red-600' : percentage > 60 ? 'text-amber-600' : 'text-green-600'
            }`}
          >
            {percentage.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {percentage !== undefined && (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                percentage > 80 ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceCard({ service }: { service: ServiceHealth }) {
  const statusIcon = {
    healthy: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
    unhealthy: <XCircleIcon className="w-5 h-5 text-red-500" />,
    degraded: <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />,
    unknown: <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />,
  };

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3">
        {statusIcon[service.status]}
        <div>
          <p className="font-medium text-gray-900">{service.name}</p>
          {service.responseTime && (
            <p className="text-sm text-gray-500">{service.responseTime}ms respuesta</p>
          )}
        </div>
      </div>
      <StatusBadge status={service.status} size="sm" />
    </div>
  );
}

export default function ServerPage() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<ServerMetrics | null>(null);
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadData();

    if (autoRefresh) {
      intervalRef.current = setInterval(loadData, 30000); // Refresh every 30s
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  const loadData = async () => {
    try {
      const [metricsRes, servicesRes] = await Promise.all([
        metricsApi.server().catch(() => ({ data: null })),
        metricsApi.services().catch(() => ({ data: { services: [] } })),
      ]);

      if (metricsRes.data) {
        setMetrics(metricsRes.data);
      }
      setServices(servicesRes.data.services || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading server data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  // Mock data for demo (remove when backend is connected)
  const mockMetrics: ServerMetrics = metrics || {
    cpu: { usage: 23.5, cores: 4, model: 'Intel Xeon E5-2680 v4' },
    memory: { total: 8589934592, used: 5368709120, free: 3221225472, usage: 62.5 },
    disk: { total: 107374182400, used: 42949672960, free: 64424509440, usage: 40 },
    uptime: 864000,
    hostname: 'vida-server-01',
    platform: 'linux',
    load: [1.2, 1.5, 1.3],
  };

  const mockServices: ServiceHealth[] = services.length > 0 ? services : [
    { name: 'MongoDB Core', status: 'healthy', responseTime: 12, lastCheck: new Date().toISOString() },
    { name: 'MongoDB Ecommerce', status: 'healthy', responseTime: 15, lastCheck: new Date().toISOString() },
    { name: 'MongoDB Customers', status: 'healthy', responseTime: 14, lastCheck: new Date().toISOString() },
    { name: 'MongoDB Catalog', status: 'healthy', responseTime: 13, lastCheck: new Date().toISOString() },
    { name: 'Redis', status: 'healthy', responseTime: 3, lastCheck: new Date().toISOString() },
    { name: 'Backend API', status: 'healthy', responseTime: 45, lastCheck: new Date().toISOString() },
    { name: 'MinIO Storage', status: 'healthy', responseTime: 23, lastCheck: new Date().toISOString() },
    { name: 'Backup Service', status: 'healthy', lastCheck: new Date().toISOString() },
  ];

  return (
    <div>
      <Header
        title="Estado del Servidor"
        subtitle={`Última actualización: ${lastUpdate.toLocaleTimeString()}`}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-vida-600 focus:ring-vida-500"
              />
              Auto-refresh
            </label>
            <button onClick={loadData} className="btn-secondary">
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Actualizar
            </button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* Server info */}
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-vida-100 rounded-xl">
              <ServerIcon className="w-8 h-8 text-vida-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{mockMetrics.hostname}</h2>
              <p className="text-gray-500">
                {mockMetrics.platform} · {mockMetrics.cpu.cores} cores · Uptime: {formatUptime(mockMetrics.uptime)}
              </p>
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="CPU"
            value={`${mockMetrics.cpu.usage.toFixed(1)}%`}
            subtitle={mockMetrics.cpu.model}
            icon={CpuChipIcon}
            color="bg-blue-100 text-blue-600"
            percentage={mockMetrics.cpu.usage}
          />
          <MetricCard
            title="Memoria RAM"
            value={formatBytes(mockMetrics.memory.used)}
            subtitle={`de ${formatBytes(mockMetrics.memory.total)}`}
            icon={CircleStackIcon}
            color="bg-green-100 text-green-600"
            percentage={mockMetrics.memory.usage}
          />
          <MetricCard
            title="Disco"
            value={formatBytes(mockMetrics.disk.used)}
            subtitle={`de ${formatBytes(mockMetrics.disk.total)}`}
            icon={CircleStackIcon}
            color="bg-amber-100 text-amber-600"
            percentage={mockMetrics.disk.usage}
          />
          <MetricCard
            title="Uptime"
            value={formatUptime(mockMetrics.uptime)}
            subtitle="Tiempo en funcionamiento"
            icon={ClockIcon}
            color="bg-purple-100 text-purple-600"
          />
        </div>

        {/* Load average */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Carga del sistema</h3>
          <div className="grid grid-cols-3 gap-4">
            {['1 min', '5 min', '15 min'].map((label, i) => (
              <div key={label} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{mockMetrics.load[i].toFixed(2)}</p>
                <p className="text-sm text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Services */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Servicios</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {mockServices.map((service) => (
              <ServiceCard key={service.name} service={service} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
